import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Cloud, Copy, FileText, FolderArchive, HardDrive, History, Printer, RefreshCw,
  ShieldCheck, Siren, Smartphone, Wifi, WifiOff, Zap,
} from "lucide-react";
import { openBlob, readEmergency, readIndex, readPdf, syncDeviceCopy } from "../lib/safeguardCache";
import { isOfflineError } from "../lib/offlineSync";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button, Card, Select, Spinner } from "./ui-kit";
import DeviceRecords from "./DeviceRecords";

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const WEEKDAYS = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];

export const itDate = (key) => (key ? `${key.slice(8, 10)}/${key.slice(5, 7)}/${key.slice(0, 4)}` : "—");
export const itDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const docCount = (n) => (n === 1 ? "1 documento" : `${n} documenti`);
const monthLabel = (key) => `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
const weekday = (key) => WEEKDAYS[new Date(`${key}T12:00:00`).getDay()];

export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}

function lastMonths(since, count = 12) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i += 1) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (since && key < since.slice(0, 7)) break;
    out.push(key);
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

function daysOfMonth(month, since, until) {
  const [y, m] = month.split("-").map(Number);
  const total = new Date(y, m, 0).getDate();
  const days = [];
  for (let d = total; d >= 1; d -= 1) {
    const key = `${month}-${String(d).padStart(2, "0")}`;
    if (until && key > until) continue;
    if (since && key < since) continue;
    days.push(key);
  }
  return days;
}

// Con la rete apre la copia del server; senza rete quella salvata su questo dispositivo (se c'è).
const openDoc = async (doc) => {
  const win = window.open("", "_blank");
  if (navigator.onLine) {
    try {
      const { data } = await api.get(`/app/safeguard/documents/${doc.id}/pdf`, { responseType: "blob" });
      return openBlob(data, win);
    } catch (error) {
      if (!isOfflineError(error)) {
        if (win) win.close();
        toast.error("Impossibile aprire il documento. Riprova.");
        return null;
      }
      // Server non raggiungibile anche se Internet c'è: si ripiega sulla copia del dispositivo.
    }
  }
  const local = await readPdf(doc).catch(() => ({ ok: false }));
  if (local.ok) {
    toast.info("Server non raggiungibile: aperta la copia verificata salvata su questo dispositivo.");
    return openBlob(local.blob, win);
  }
  if (win) win.close();
  toast.error("Server non raggiungibile e documento non salvato su questo dispositivo.");
  return null;
};

function CountsLine({ counts = {} }) {
  return (
    <span className="text-xs text-slate-500">
      {counts.registrations ?? counts.done ?? 0} registrazioni
      {counts.missing ? <span className="text-red-600"> · {counts.missing} non registrati</span> : null}
      {counts.ncs ? <span className="text-amber-700"> · {counts.ncs} NC</span> : null}
    </span>
  );
}

function StatusRow({ icon: Icon, tone = "ok", title, detail, testid, children }) {
  const tones = { ok: "text-emerald-600 bg-emerald-50", warn: "text-amber-700 bg-amber-50", off: "text-slate-500 bg-slate-100", bad: "text-red-600 bg-red-50" };
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg ${tones[tone]}`}><Icon className="h-4 w-4" strokeWidth={1.75} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-navy">{title}</p>
        {detail && <p className="text-xs text-slate-500">{detail}</p>}
        {children}
      </div>
    </div>
  );
}

// Pulsante rosso, ben visibile: la procedura cartacea va stampata PRIMA di un'emergenza.
// Con il server apre la versione aggiornata; se il server non risponde, quella salvata sul dispositivo.
export async function openEmergency() {
  const win = window.open("", "_blank");
  if (navigator.onLine) {
    try {
      const { data } = await api.get("/app/safeguard/emergency-procedure", { responseType: "blob" });
      return openBlob(data, win);
    } catch (error) {
      if (!isOfflineError(error)) {
        if (win) win.close();
        toast.error("Impossibile aprire la procedura di emergenza. Riprova.");
        return null;
      }
    }
  }
  const local = await readEmergency();
  if (local) {
    toast.info("Server non raggiungibile: aperta la procedura salvata su questo dispositivo.");
    return openBlob(local, win);
  }
  if (win) win.close();
  toast.error("Server non raggiungibile e procedura non ancora salvata su questo dispositivo.");
  return null;
}

function EmergencyButton() {
  return (
    <button type="button" onClick={openEmergency} data-testid="safeguard-emergency-pdf"
      className="group mt-4 flex w-full max-w-xl items-center gap-4 rounded-xl bg-gradient-to-r from-red-600 to-red-700 px-4 py-3.5 text-left text-white shadow-lg shadow-red-950/40 ring-1 ring-red-400/40 transition-[transform,box-shadow,background-color] hover:-translate-y-0.5 hover:from-red-500 hover:to-red-600 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy sm:px-5">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-white/15 ring-1 ring-white/25">
        <Siren className="h-6 w-6" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-heading text-lg font-semibold leading-tight sm:text-xl">Stampa la procedura di emergenza</span>
        <span className="mt-0.5 block text-xs text-red-50/90 sm:text-sm">Da tenere in azienda: cosa fare se l’app non funziona, QR dell’archivio DF Drive e registro cartaceo</span>
      </span>
      <Printer className="hidden h-5 w-5 shrink-0 opacity-80 transition-transform group-hover:translate-x-0.5 sm:block" strokeWidth={1.75} />
    </button>
  );
}

export default function SafeGuardArchive() {
  const { user } = useAuth();
  const online = useOnline();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);
  const [month, setMonth] = useState(null);
  const [daily, setDaily] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [device, setDevice] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [openVersions, setOpenVersions] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const [{ data: s }, { data: m }] = await Promise.all([
        api.get("/app/safeguard/status"),
        api.get("/app/safeguard/documents", { params: { kind: "monthly" } }),
      ]);
      setStatus(s);
      setMonthly(m.documents);
      setMonth((current) => current || (s.last_daily?.period_key || s.last_documentable_day).slice(0, 7));
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { loadStatus(); readIndex().then(setDevice); }, [loadStatus]);

  useEffect(() => {
    if (!month) return;
    setDaily(null);
    api.get("/app/safeguard/documents", { params: { kind: "daily", month, include_history: true } })
      .then(({ data }) => setDaily(data.documents)).catch(() => setDaily([]));
  }, [month]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const index = await syncDeviceCopy(user, { force: true });
      if (index) { setDevice(index); toast.success(`Salvati su questo dispositivo: ${docCount(index.documents.length)} verificati.`); }
      else toast.info("Copia sul dispositivo non disponibile in questa sessione.");
    } catch {
      toast.error("Non è stato possibile aggiornare la copia sul dispositivo. Riprova.");
    } finally {
      setSyncing(false);
    }
  };

  const byDay = useMemo(() => {
    const map = {};
    for (const doc of daily || []) (map[doc.period_key] ||= []).push(doc);
    Object.values(map).forEach((list) => list.sort((a, b) => b.version - a.version));
    return map;
  }, [daily]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(status.dfdrive.share_url); toast.success("Link copiato."); }
    catch { toast.info(status.dfdrive.share_url); }
  };

  if (error) {
    return (
      <Card className="p-5 sm:p-6" data-testid="safeguard-error">
        <p className="font-medium text-navy">Archivio SafeGuard non raggiungibile in questo momento.</p>
        <p className="mt-1 text-sm text-slate-500">Puoi aprire i documenti salvati su questo dispositivo{device?.share_url ? " o l'archivio di sicurezza su DF Drive" : ""}.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/ispezione-offline"><Button variant="outline" data-testid="safeguard-open-offline"><Smartphone className="h-4 w-4" />Documenti su questo dispositivo</Button></Link>
          <Button variant="ghost" onClick={loadStatus}><RefreshCw className="h-4 w-4" />Riprova</Button>
        </div>
        <div className="mt-5"><DeviceRecords /></div>
      </Card>
    );
  }
  if (!status) return <Card className="grid place-items-center p-10"><Spinner /></Card>;

  const last = status.last_daily;
  const drive = status.dfdrive || {};
  const missing = status.missing_days || [];
  const monthsList = lastMonths(status.active_since);
  const days = month ? daysOfMonth(month, status.active_since, status.last_documentable_day) : [];

  return (
    <div className="space-y-6" data-testid="safeguard-archive">
      {/* Ultimo report e stato delle copie */}
      <div className="overflow-hidden rounded-xl bg-navy text-white shadow-card">
        <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-gold-bright"><ShieldCheck className="h-4 w-4" strokeWidth={1.5} />Archivio verificato · DF HACCP SafeGuard</p>
            {last ? (
              <>
                <h2 className="mt-3 font-heading text-2xl font-semibold sm:text-3xl" data-testid="safeguard-covered-until">
                  Registrazioni documentate fino al {itDate(last.period_key)}
                </h2>
                <p className="mt-2 text-sm text-white/70">
                  Ultimo report: {last.doc_number}{last.version > 1 ? ` (versione ${last.version})` : ""} · generato il {itDateTime(last.generated_at)}
                </p>
                <p className="mt-1 break-all font-mono text-[11px] text-white/45">SHA-256 {last.sha256}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button variant="accent" onClick={() => openDoc(last)} data-testid="safeguard-open-last"><FileText className="h-4 w-4" />Apri e stampa l’ultimo report</Button>
                  {monthly[0] && <Button variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={() => openDoc(monthly[0])} data-testid="safeguard-open-last-monthly"><FolderArchive className="h-4 w-4" />Fascicolo di {monthLabel(monthly[0].period_key)}</Button>}
                </div>
              </>
            ) : (
              <>
                <h2 className="mt-3 font-heading text-2xl font-semibold">Il primo report sarà disponibile dopo le 00:15</h2>
                <p className="mt-2 text-sm text-white/70">Ogni notte DF HACCP archivia e verifica le registrazioni del giorno precedente.</p>
              </>
            )}
            <EmergencyButton />
          </div>
          <div className="space-y-4 rounded-xl bg-white p-4 text-navy sm:p-5">
            <StatusRow icon={online ? Wifi : WifiOff} tone={online ? "ok" : "warn"} testid="safeguard-connection"
              title={online ? "Connesso" : "Senza connessione"}
              detail={online ? "Archivio completo consultabile." : "Restano disponibili i documenti salvati su questo dispositivo."} />
            <StatusRow icon={ShieldCheck} tone={status.last_verified_at ? "ok" : "off"} testid="safeguard-primary"
              title="Archivio principale" detail={status.last_verified_at ? `Ultima verifica: ${itDateTime(status.last_verified_at)}` : "Nessun documento ancora verificato"} />
            <StatusRow icon={Cloud} tone={drive.last_copy_at ? (drive.last_check_ok === false ? "bad" : "ok") : "off"} testid="safeguard-dfdrive"
              title="Copia di sicurezza su DF Drive"
              detail={drive.last_copy_at ? `Ultima copia verificata: ${itDateTime(drive.last_copy_at)}` : "Non ancora disponibile"} />
            <StatusRow icon={HardDrive} tone={device?.documents?.length ? "ok" : "off"} testid="safeguard-device"
              title="Copia su questo dispositivo"
              detail={device?.documents?.length ? `${docCount(device.documents.length)} · aggiornata il ${itDateTime(device.saved_at)}` : user?.impersonated_by ? "Non salvata durante “Entra come”: accedi con un utente dell’azienda" : "Non ancora salvata"}>
              {!user?.impersonated_by && (
                <button type="button" onClick={syncNow} disabled={syncing || !online}
                  className="mt-1 inline-flex min-h-[32px] items-center gap-1.5 text-xs font-medium text-gold underline-offset-2 hover:underline disabled:opacity-50" data-testid="safeguard-sync-device">
                  <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Aggiornamento…" : "Aggiorna ora"}
                </button>
              )}
            </StatusRow>
          </div>
        </div>
        {missing.length > 0 && (
          <div className="flex items-start gap-2 border-t border-white/10 bg-red-500/15 px-5 py-3 text-sm text-red-100 sm:px-7" data-testid="safeguard-missing-days">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Report non ancora disponibile per: {missing.map(itDate).join(", ")}. Il sistema riprova automaticamente; per questi giorni non esiste ancora un documento archiviato.</span>
          </div>
        )}
      </div>

      <DeviceRecords />

      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* Storico giornaliero */}
        <Card className="p-5 sm:p-6" data-testid="safeguard-daily-history">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="font-heading text-xl font-semibold">Storico giornaliero</h3>
              <p className="text-sm text-slate-500">Un report per ogni giorno, archiviato e verificato.</p>
            </div>
            <Select aria-label="Mese dello storico" value={month || ""} onChange={(e) => setMonth(e.target.value)} className="w-auto min-w-[170px]" data-testid="safeguard-month">
              {monthsList.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </Select>
          </div>
          <ul className="mt-4 divide-y divide-hairline" data-testid="safeguard-daily-list">
            {daily === null && <li className="py-6 text-center"><Spinner /></li>}
            {daily !== null && days.length === 0 && <li className="py-6 text-sm text-slate-500">Nessun giorno da documentare in questo mese.</li>}
            {daily !== null && days.map((day) => {
              const versions = byDay[day] || [];
              const current = versions.find((v) => !v.superseded_by) || versions[0];
              return (
                <li key={day} className="py-3" data-testid={`safeguard-day-${day}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="w-24 shrink-0">
                      <p className="font-medium text-navy">{itDate(day).slice(0, 5)}</p>
                      <p className="text-xs uppercase tracking-wider text-slate-400">{weekday(day)}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      {current ? (
                        <>
                          <CountsLine counts={current.counts} />
                          {current.version > 1 && (
                            <button type="button" onClick={() => setOpenVersions(openVersions === day ? null : day)}
                              className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700" data-testid={`safeguard-versions-${day}`}>
                              <History className="h-3 w-3" />Versione {current.version}
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="text-xs font-medium text-red-600">Report non disponibile</span>
                      )}
                    </div>
                    {current && <Button variant="ghost" className="px-3" onClick={() => openDoc(current)} data-testid={`safeguard-open-${day}`}><FileText className="h-4 w-4" />Apri</Button>}
                  </div>
                  {openVersions === day && (
                    <ul className="ml-24 mt-2 space-y-1 border-l-2 border-hairline pl-3 text-xs text-slate-500">
                      {versions.map((v) => (
                        <li key={v.id} className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-navy">v{v.version}</span>
                          <span>{itDateTime(v.generated_at)}</span>
                          <span>{v.superseded_by ? "sostituita" : "in vigore"}{v.reason ? ` · ${v.reason}` : ""}</span>
                          <button type="button" className="text-gold underline" onClick={() => openDoc(v)}>apri</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>

        <div className="space-y-6">
          {/* Fascicoli mensili */}
          <Card className="p-5 sm:p-6" data-testid="safeguard-monthly">
            <h3 className="font-heading text-xl font-semibold">Fascicoli mensili</h3>
            <p className="text-sm text-slate-500">Indice, riepilogo, controlli mancanti e report del mese in un unico PDF.</p>
            <ul className="mt-3 divide-y divide-hairline">
              {monthly.length === 0 && <li className="py-3 text-sm text-slate-500">Il primo fascicolo sarà generato il giorno 1 del mese prossimo.</li>}
              {monthly.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 py-3">
                  <FolderArchive className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium capitalize text-navy">{monthLabel(doc.period_key)}</p>
                    <span className="text-xs text-slate-500">{doc.counts?.days_with_report ?? 0} giorni documentati{doc.version > 1 ? ` · versione ${doc.version}` : ""}</span>
                  </div>
                  <Button variant="ghost" className="px-3" onClick={() => openDoc(doc)} data-testid={`safeguard-open-monthly-${doc.period_key}`}><FileText className="h-4 w-4" />Apri</Button>
                </li>
              ))}
            </ul>
          </Card>

          {/* Se il server non risponde */}
          <Card className="p-5 sm:p-6" data-testid="safeguard-emergency">
            <h3 className="font-heading text-xl font-semibold">Se l’app non funziona</h3>
            <ol className="mt-3 space-y-3 text-sm text-slate-600">
              <li className="flex gap-3"><Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-gold" /><span>Dalla schermata di accesso tocca <strong>“Documenti salvati su questo dispositivo”</strong>: si aprono senza Internet.
                <Link to="/ispezione-offline" className="mt-2 flex min-h-[36px] w-fit items-center gap-1.5 rounded-lg border border-navy px-3 text-xs font-medium text-navy hover:bg-slate-50" data-testid="safeguard-try-offline"><Smartphone className="h-3.5 w-3.5" />Apri ora la consultazione senza server</Link></span></li>
              {drive.share_url && (
                <li className="flex gap-3"><Cloud className="mt-0.5 h-4 w-4 shrink-0 text-gold" /><span>Apri l’<strong>archivio di sicurezza su DF Drive</strong>, separato dal server dell’app.
                  <span className="mt-2 flex flex-wrap gap-2">
                    <a href={drive.share_url} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-1.5 px-4 text-sm rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 font-semibold text-white shadow-md shadow-emerald-900/20 ring-1 ring-emerald-400/40 transition-[transform,background-color] hover:-translate-y-0.5 hover:from-emerald-500 hover:to-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2" data-testid="safeguard-dfdrive-link"><Cloud className="h-3.5 w-3.5" />Apri DF Drive</a>
                    <button type="button" onClick={copyLink} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-xs font-medium text-navy hover:bg-slate-100"><Copy className="h-3.5 w-3.5" />Copia link</button>
                  </span></span></li>
              )}
              <li className="flex gap-3"><FileText className="mt-0.5 h-4 w-4 shrink-0 text-gold" /><span>Per i controlli che non riesci a registrare usa i <Link to="/app/report" className="font-medium text-navy underline">moduli cartacei di emergenza</Link> e inseriscili nell’app appena torna disponibile.</span></li>
            </ol>
            <p className="mt-4 text-xs text-slate-500">La <strong>procedura di emergenza</strong> da stampare (pulsante rosso in alto) contiene il QR dell’archivio DF Drive e un registro da compilare a mano.</p>
            <p className="mt-4 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              Ogni documento riporta il periodo coperto e l’ora fino a cui i dati erano salvati sul server. L’impronta SHA-256 permette di verificare che il file non sia stato modificato; non è una firma digitale.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Sezione premium della Panoramica: accesso evidente a Ispezione ASL/NAS con lo stato dell'archivio.
// onQuickPrint: tasto viola «Stampa rapida ispezione» (sintesi del mese in un solo PDF) accanto al titolo.
export function SafeGuardStatusStrip({ onQuickPrint }) {
  const online = useOnline();
  const [status, setStatus] = useState(null);
  const [device, setDevice] = useState(null);
  useEffect(() => {
    api.get("/app/safeguard/status").then(({ data }) => setStatus(data)).catch(() => setStatus(false));
    readIndex().then(setDevice);
  }, []);
  const last = status?.last_daily;
  const copy = status?.dfdrive?.last_copy_at || status?.last_verified_at;
  const missing = status?.missing_days?.length || 0;
  const tiles = [
    { label: "Archivio verificato fino al", value: last ? itDate(last.period_key) : "—", testid: "safeguard-strip-last" },
    { label: "Ultima copia di sicurezza", value: copy ? itDateTime(copy) : "—" },
    { label: "Su questo dispositivo", value: device?.documents?.length ? docCount(device.documents.length) : "Non ancora salvati" },
    { label: "Connessione", value: online ? "Online" : "Offline", tone: online ? "text-emerald-300" : "text-amber-300", testid: "safeguard-strip-connection" },
  ];
  return (
    <section className="relative overflow-hidden rounded-2xl border border-navy bg-navy text-white shadow-card" data-testid="safeguard-strip">
      <div className="absolute inset-0 bg-[radial-gradient(120%_140%_at_100%_0%,rgba(56,189,248,0.22),transparent_55%)]" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gold via-gold-bright to-[#14C38E]" aria-hidden="true" />
      <ShieldCheck className="pointer-events-none absolute -bottom-10 -right-8 h-56 w-56 text-white/[0.05]" strokeWidth={1} aria-hidden="true" />
      <div className="relative grid gap-6 p-5 sm:p-7 lg:grid-cols-[1.1fr_1.4fr] lg:items-center lg:gap-10">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gold-bright">Modalità ispezione organi di controllo</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-3">
            <h2 className="flex items-center gap-3 font-heading text-3xl font-bold leading-tight sm:text-4xl">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/10 ring-1 ring-white/15"><ShieldCheck className="h-6 w-6 text-gold-bright" strokeWidth={1.5} /></span>
              Ispezione ASL/NAS
            </h2>
            {onQuickPrint && <button type="button" onClick={onQuickPrint}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-violet-600 px-4 text-sm font-semibold text-white shadow-lg shadow-black/20 transition-colors hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
              aria-label="Stampa rapida per l'ispezione ASL/NAS: sintesi del mese in corso"
              title="Temperature, pulizie, altri controlli e merci in entrata del mese in corso, in un solo PDF"
              data-testid="btn-stampa-rapida-ispezione">
              <Zap className="h-4 w-4" fill="currentColor" /> Stampa rapida ispezione
            </button>}
          </div>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-white/70">
            Il registro verificato delle vostre registrazioni HACCP, pronto da mostrare e stampare in caso di controllo, anche se l’app o Internet non funzionano.
          </p>
          <Link to="/app/ispezione" className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-gold px-6 text-sm font-semibold text-white shadow-lg shadow-black/20 transition-colors hover:bg-gold-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright focus-visible:ring-offset-2 focus-visible:ring-offset-navy" data-testid="safeguard-strip-open">
            <FileText className="h-4 w-4" />Apri Ispezione ASL/NAS
          </Link>
        </div>
        <div className="min-w-0">
          <div className="grid grid-cols-2 gap-3">
            {tiles.map((t) => (
              <div key={t.label} className="min-w-0 rounded-xl bg-white/[0.07] p-3.5 ring-1 ring-white/10 sm:p-4" data-testid={t.testid}>
                <p className="text-[11px] uppercase tracking-wider text-white/50">{t.label}</p>
                <p className={`mt-1 break-words font-heading text-lg font-semibold sm:text-xl ${t.tone || "text-white"}`}>{t.value}</p>
              </div>
            ))}
          </div>
          {missing > 0 && (
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-red-500/15 px-3.5 py-2.5 text-xs text-red-100 ring-1 ring-red-400/30">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{missing === 1 ? "1 giorno senza report archiviato" : `${missing} giorni senza report archiviato`}: il sistema riprova automaticamente.
            </p>
          )}
          {status === false && <p className="mt-3 text-xs text-white/60">Stato dell’archivio non disponibile in questo momento.</p>}
        </div>
      </div>
    </section>
  );
}
